/**
 * Per-course exam date input. Shows the saved override (or hint if none) and
 * lets the student set/clear a date that overrides their global target.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarDays, X, Loader2 } from "lucide-react";

export default function CourseExamDateInput({ courseId, allDates }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  // Initialize from passed-in dict (parent fetches once for all courses)
  useEffect(() => {
    setValue((allDates && allDates[courseId]) || "");
  }, [courseId, allDates]);

  const save = useCallback(async (nextVal) => {
    setBusy(true);
    try {
      await api.patch(`/student/enrollment/${courseId}/exam-date`, { target_exam_date: nextVal || null });
      setValue(nextVal || "");
      toast.success(nextVal ? "Exam date saved for this course" : "Override cleared — using global exam date");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  }, [courseId]);

  if (!editing && !value) {
    return (
      <button
        type="button"
        className="mt-3 text-[11px] text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
        onClick={() => setEditing(true)}
        data-testid={`course-exam-date-set-${courseId}`}
      >
        <CalendarDays className="w-3 h-3" /> Set exam date for this course
      </button>
    );
  }

  return (
    <div className="mt-3 flex items-center gap-2" data-testid={`course-exam-date-row-${courseId}`}>
      <CalendarDays className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      <Input
        type="date"
        value={value}
        disabled={busy}
        onChange={(e) => save(e.target.value)}
        className="h-8 text-xs"
        data-testid={`course-exam-date-input-${courseId}`}
      />
      {value && !busy && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
          onClick={() => save("")}
          data-testid={`course-exam-date-clear-${courseId}`}
          aria-label="Clear exam date"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      )}
      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
    </div>
  );
}
